import { CSSProperties, ForwardedRef, forwardRef, memo, ReactNode, SyntheticEvent, useEffect, useRef } from 'react';
import cls from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { useToggle } from '@cutwise/react-utils';

import { stopPropagation } from '@/common/helpers/stopPropagation';

import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import ModalPortal from '../ModalPortal/ModalPortal';

import styles from './Modal.module.css';
// Modal use modalStore directly, because it can be run outside mobx context via confirmationFactory
import { modalStore } from './ModalStore';

export const CENTER = 'center';
export const TOP = 'top';
export const BOTTOM = 'bottom';
export const LEFT = 'left'; // fixme: doesn't work
export const RIGHT = 'right'; // fixme: doesn't work

const CLASS_POSITION_MAP = {
  [CENTER]: styles.modalCentered,
  [TOP]: styles.modalTopped,
  [RIGHT]: styles.modalRighted,
  [BOTTOM]: styles.modalBottomed,
  [LEFT]: styles.modalLefted,
};

type ModalPositionType = keyof typeof CLASS_POSITION_MAP;
export type ModalCloseEvent = SyntheticEvent | KeyboardEvent;

interface ModalProps {
  onClose?: (e: ModalCloseEvent) => void;
  isShowed?: boolean;
  usePortal?: boolean;
  title?: ReactNode;
  style?: CSSProperties;
  position?: ModalPositionType;
  renderModalFooter?: () => ReactNode;
  stretch?: boolean;
  wide?: boolean;
  fitWidthByContent?: boolean;
  disableClose?: boolean;
  isCloseHidden?: boolean;
  children?: ReactNode;
  contentClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  closeClassName?: string;
  childrenClassName?: string;
  bodyContentClassName?: string;
  footerClassName?: string;
}

const Modal = forwardRef((props: ModalProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    onClose,
    isShowed = false,
    usePortal = true,
    title,
    style,
    position = TOP,
    renderModalFooter,
    stretch = false,
    wide = false,
    fitWidthByContent = false,
    disableClose = false,
    isCloseHidden = false,
    children,
    contentClassName,
    headerClassName,
    titleClassName,
    closeClassName,
    childrenClassName,
    bodyContentClassName,
    footerClassName,
  } = props;

  const backdropElement = useRef<Nullable<HTMLDivElement>>(null);
  const childrenRef = useRef<Nullable<HTMLDivElement>>(null);
  const modalIdRef = useRef<string>(uniqueId());

  const [isScrollVisible, showScroll, hideScroll] = useToggle();

  function onBackdropClick(e: SyntheticEvent) {
    if (e.target !== backdropElement.current) {
      return;
    }

    if (disableClose) {
      return;
    }

    onClose?.(e);
  }

  function onCloseHandler(e: SyntheticEvent) {
    if (disableClose) {
      return;
    }

    onClose?.(e);
  }

  function onPressEscKey(e: KeyboardEvent) {
    if (e.code !== 'Escape' || !modalStore.isTopModal(modalIdRef.current)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (disableClose) {
      return;
    }

    onClose?.(e);
  }

  function getScrollState() {
    const childrenHeight = childrenRef.current?.offsetHeight;
    const contentHeight = (childrenRef.current?.parentNode as HTMLElement)?.offsetHeight;

    if (childrenHeight && contentHeight && childrenHeight > contentHeight) {
      showScroll();
    } else {
      hideScroll();
    }
  }

  function removeListeners() {
    modalStore.removeOpenModalId(modalIdRef.current);
    document.removeEventListener('keydown', onPressEscKey, false);
    window.removeEventListener('resize', getScrollState);
  }

  useEffect(() => {
    const observer = new MutationObserver(getScrollState);

    if (isShowed) {
      if (childrenRef.current) {
        observer.observe(childrenRef.current, { childList: true, subtree: true });
      }

      getScrollState();

      modalStore.addOpenModalId(modalIdRef.current);
      document.addEventListener('keydown', onPressEscKey, false);
      window.addEventListener('resize', getScrollState);
    } else {
      observer.disconnect();
      removeListeners();
    }

    return () => {
      observer.disconnect();
      removeListeners();
    };
  }, [isShowed, disableClose]);

  const isCloseVisible = !!onClose && !isCloseHidden;

  const modal = isShowed ? (
    <div
      aria-modal="true"
      className={cls(styles.modal, CLASS_POSITION_MAP[position])}
      role="dialog"
      style={style}
      // React events from a portal bubble up the React tree, not the DOM tree, so a modal rendered
      // inside a link or a clickable card would trigger its parent's handlers (e.g. navigation).
      onClick={stopPropagation}
    >
      <div
        ref={backdropElement}
        aria-label="Modal backdrop"
        className={styles.modalBackdrop}
        onClick={onBackdropClick}
      />

      <div
        className={cls(
          styles.modalInner,
          {
            [styles.modalInnerStretched]: stretch,
            [styles.modalInnerWide]: wide,
            [styles.modalInnerFitByContent]: fitWidthByContent,
          },
          contentClassName,
        )}
      >
        <div className={cls(styles.modalHeader, headerClassName)}>
          {title && <h2 className={cls(styles.modalTitle, titleClassName)}>{title}</h2>}

          {isCloseVisible && (
            <div className={cls(styles.modalControls, closeClassName)}>
              <Button aria-label="Close Modal" color="transparent" onClick={onCloseHandler} withIcon>
                <Icon name="close" />
              </Button>
            </div>
          )}
        </div>

        <div className={cls(styles.modalContent, bodyContentClassName)}>
          <div
            ref={ref}
            className={cls(styles.modalContentWrapper, { [styles.modalContentWithScroll]: isScrollVisible })}
          >
            <div ref={childrenRef} className={cls(styles.modalChildren, childrenClassName)}>
              {children}
            </div>
          </div>
        </div>

        {renderModalFooter ? (
          <div className={cls(styles.modalFooter, footerClassName)}>{renderModalFooter()}</div>
        ) : null}
      </div>
    </div>
  ) : null;

  return usePortal ? <ModalPortal>{modal}</ModalPortal> : modal;
});

export type { ModalProps };
export default memo(Modal);
